import { MailOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input, Typography, message } from "antd";
import { useAuth } from "authentication";
import { useHistory } from "react-router-dom";
import { httpClient } from "utils/Api";
import AppConfig from "utils/Config";

const ForgotPassword = () => {
  const { Title } = Typography;
  const [form] = Form.useForm();
  const history = useHistory();
  const { userSignOut } = useAuth();

  const forgotPasswordMutation = useMutation({
    mutationFn: (email) =>
      httpClient.post("/forgot-password/request/", { email }),
    onSuccess: () => {
      message.success(
        "Password reset email sent! Check your inbox for the reset link.",
      );
      userSignOut();
      form.resetFields();
      setTimeout(() => {
        history.push("/login");
      }, 1500);
    },
    onError: (error) => {
      const errorMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.email?.[0] ||
        "Failed to send reset link. Please verify your email and try again.";
      message.error(errorMsg);
    },
  });

  const onFinish = async (values) => {
    forgotPasswordMutation.mutate(values.email);
  };

  return (
    <div className="min-h-screen bg-linear-to-b from-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-4">
          <img src={AppConfig.logo} alt="logo" width={100} />
        </div>

        <div className="bg-white rounded-lg shadow-lg p-5 mb-2">
          <Title level={4} className="text-center mb-2">
            Reset Your Password
          </Title>
          <p className="text-gray-600 text-center mb-6 text-sm">
            Enter your email address and we&apos;ll send you a link to reset
            your password.
          </p>

          {!forgotPasswordMutation.isPending && (
            <Form
              form={form}
              name="forgot-password"
              onFinish={onFinish}
              className="space-y-6!"
              layout="vertical"
            >
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please input your email!",
                  },
                  {
                    type: "email",
                    message: "Please enter a valid email!",
                  },
                ]}
              >
                <Input
                  placeholder="Email Address"
                  prefix={<MailOutlined className="mr-2" />}
                  type="email"
                  className="h-10"
                />
              </Form.Item>

              <Button
                type="primary"
                block
                htmlType="submit"
                loading={forgotPasswordMutation.isPending}
                className="h-10 mt-6"
              >
                Send Reset Link
              </Button>
            </Form>
          )}

          <p className="text-center text-sm text-gray-600 mb-0">
            Remember your password?{" "}
            <a
              href="/login"
              className="text-blue-600 font-semibold hover:text-blue-800"
            >
              Sign In
            </a>
          </p>
        </div>

        <p className="text-center text-sm">{AppConfig.tagLine}</p>
      </div>
    </div>
  );
};

export default ForgotPassword;
